import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus } from 'lucide-react';
import Loader from 'components/Loader/Loader';
import PortfolioProjectCard from './PortfolioProjectCard';
import styles from './PortfolioProjects.module.scss';

const DESKTOP_LIMIT = 6;
const PHONE_LIMIT = 3;
// Drag to reorder needs a mouse. Touch screens keep the ⋯ menu.
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

const PortfolioProjects = ({
  credits,
  total,
  isOwn,
  isReorderingCredits,
  loadingCredits,
  moreCredits,
  onLoadMore,
  onReorderCredit,
  user,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [finePointer, setFinePointer] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  // One card plays at a time; starting another stops the first.
  const [playingId, setPlayingId] = useState(null);

  // Read after mount so the server and first client render agree.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const query = window.matchMedia(FINE_POINTER);
    const update = () => setFinePointer(query.matches);
    update();
    if (query.addEventListener) {
      query.addEventListener('change', update);
      return () => query.removeEventListener('change', update);
    }
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);
  const loaded =
    typeof credits[0] === 'object' &&
    Object.prototype.hasOwnProperty.call(credits[0], 'id');
  // Shown projects, from the credits API. Loaded cards can briefly exceed it
  // while a reorder or delete settles.
  const count = Math.max(total || 0, credits.length);

  if (!isOwn && count === 0) {
    return null;
  }

  const itemClassName = (index) => {
    if (showAll) return '';
    if (index >= DESKTOP_LIMIT) return styles.collapsed;
    if (index >= PHONE_LIMIT) return styles.collapsedOnPhone;
    return '';
  };

  const handleShowAll = () => {
    if (!showAll && moreCredits && !loadingCredits) {
      onLoadMore();
    }
    setShowAll(!showAll);
  };

  const canDrag =
    isOwn &&
    finePointer &&
    loaded &&
    credits.length > 1 &&
    !isReorderingCredits;

  const endDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  // No dragging a playing card: scrubbing its controls would start a drag.
  const dragProps = (credit, idx) =>
    canDrag && playingId !== credit.project?.id
      ? {
          draggable: true,
          isDragging: dragIndex === idx,
          isDropTarget: dropIndex === idx && dragIndex !== idx,
          onDragStart: (event) => {
            event.dataTransfer.effectAllowed = 'move';
            // Firefox only starts a drag once some data is set.
            event.dataTransfer.setData('text/plain', String(credit.id));
            setDragIndex(idx);
          },
          onDragOver: (event) => {
            if (dragIndex === null) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            if (dropIndex !== idx) setDropIndex(idx);
          },
          onDrop: (event) => {
            event.preventDefault();
            if (dragIndex !== null && dragIndex !== idx) {
              const dragged = credits[dragIndex];
              onReorderCredit(dragged.id, idx + 1);
            }
            endDrag();
          },
          onDragEnd: endDrag,
        }
      : {};

  const showAllLabel = showAll
    ? 'Show fewer projects'
    : `See all ${count} projects`;

  return (
    <section
      id="portfolio-credits"
      className={styles.section}
      aria-labelledby="portfolio-projects-title"
    >
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 id="portfolio-projects-title" className={styles.sectionTitle}>
            Projects
            {count > 0 && <span className={styles.count}> · {count}</span>}
          </h2>
          {isOwn && count > 1 && (
            <span className={styles.hint}>
              {canDrag
                ? 'Drag projects or use ⋯ to change the order'
                : 'Use ⋯ on a project to change the order'}
            </span>
          )}
        </div>
        {isOwn ? (
          <a href="/projects/create" className={styles.addButton}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            Add a project
          </a>
        ) : (
          count > DESKTOP_LIMIT &&
          !showAll && (
            <button
              type="button"
              className={`${styles.link} ${styles.desktopOnly}`}
              onClick={handleShowAll}
            >
              See all {count}
            </button>
          )
        )}
      </div>
      {/* Infinite scroll keys off this id, so only expose it when expanded. */}
      <div
        id={showAll ? 'portfolio-credits-grid' : undefined}
        className={styles.grid}
      >
        {loaded &&
          credits.map((credit, idx) => (
            <PortfolioProjectCard
              key={`portfolio-content-${credit.id}`}
              className={itemClassName(idx)}
              disabled={isReorderingCredits}
              isOwn={isOwn}
              project={credit.project}
              roles={credit.roles}
              showMoveToTop={idx > 0}
              showMoveUp={idx > 0}
              showMoveDown={idx < credits.length - 1}
              onMoveToTop={() => onReorderCredit(credit.id, 1)}
              onMoveUp={() => onReorderCredit(credit.id, idx)}
              onMoveDown={() => onReorderCredit(credit.id, idx + 2)}
              user={user}
              isPlaying={!!credit.project && playingId === credit.project.id}
              onPlay={() => setPlayingId(credit.project.id)}
              onStopPlaying={() => setPlayingId(null)}
              {...dragProps(credit, idx)}
            />
          ))}
        {isOwn && (
          <a href="/projects/create" className={styles.addTile}>
            <span className={styles.addTileIcon} aria-hidden="true">
              <Plus size={20} strokeWidth={2.5} />
            </span>
            <span className={styles.addTileTitle}>Add a project</span>
            <span className={styles.addTileText}>
              Images, video, audio or a link. Clients hire from examples, so add
              several.
            </span>
          </a>
        )}
      </div>
      {loadingCredits && (
        <div className={styles.loader}>
          <Loader loading />
        </div>
      )}
      {count > PHONE_LIMIT && (
        <button
          type="button"
          className={`${styles.showAll} ${
            !showAll && count <= DESKTOP_LIMIT ? styles.phoneOnly : ''
          }`}
          onClick={handleShowAll}
          aria-expanded={showAll}
        >
          {showAllLabel}
        </button>
      )}
    </section>
  );
};

PortfolioProjects.propTypes = {
  credits: PropTypes.array,
  total: PropTypes.number,
  isOwn: PropTypes.bool,
  isReorderingCredits: PropTypes.bool,
  loadingCredits: PropTypes.bool,
  moreCredits: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onReorderCredit: PropTypes.func,
  user: PropTypes.object,
};

PortfolioProjects.defaultProps = {
  credits: [],
};

export default PortfolioProjects;
